import React from 'react'
import { createPortal } from 'react-dom'

import { useLocation } from 'react-router-dom'
import {
  answerUpdateReadiness,
  canReloadForUpdate,
  isUpdateSafePath,
  requestWaitingUpdate,
  reloadForUpdate,
} from './updateSafety'
import { usePwaUpdateStore } from './pwaUpdateStore'
import { registerSW } from 'virtual:pwa-register'

import { useI18n } from '@runtime/core/i18n/i18n'
import styles from '@/core/pwa/PwaUpdatePrompt.module.scss'
import { watchSwUpdateDownload } from '@/core/pwa/watchSwUpdateDownload'
import { FormLine } from '@runtime/components/ui/Form'
import { Button } from '@runtime/components/ui/Button'
import { ModalContent, ModalTitle } from '@runtime/components/ui/Modal'
const { useEffect, useRef, useState } = React

const ANIM_MS = 180

type PwaUpdatePromptProps = {
  onUpdateDownloadChange?: (isDownloading: boolean) => void
}

export default function PwaUpdatePrompt({ onUpdateDownloadChange }: PwaUpdatePromptProps) {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const openRequest = usePwaUpdateStore(state => state.openRequest)
  const setAvailable = usePwaUpdateStore(state => state.setAvailable)

  const [reason, setReason] = useState<string | null>(null)
  const applying = usePwaUpdateStore(state => state.applying)
  const setApplying = usePwaUpdateStore(state => state.setApplying)
  const panelRef = useRef<HTMLDivElement>(null)
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined)
  const acceptedRef = useRef(false)
  const activatedRef = useRef(false)
  const previousFocus = useRef<HTMLElement | null>(null)
  const [needRefresh, setNeedRefresh] = useState(false)

  const [present, setPresent] = useState(false)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    if (openRequest) setNeedRefresh(true)
  }, [openRequest])

  const needRefreshDelayRef = useRef<number | null>(null)

  // Register the SW in production and expose a small in-app prompt when an update is available.
  useEffect(() => {
    if (!import.meta.env.PROD) return

    const stopAnswering = answerUpdateReadiness()
    let stopWatchingUpdate: (() => void) | null = null

    try {
      registerSW({
        immediate: true,
        onRegisteredSW(_, registration) {
          registrationRef.current = registration
          stopWatchingUpdate = watchSwUpdateDownload(registration, isDownloading => {
            onUpdateDownloadChange?.(isDownloading)
          })
        },
        onNeedReload() {
          setApplying(false)
          activatedRef.current = true
          if (acceptedRef.current && canReloadForUpdate()) reloadForUpdate()
          else setNeedRefresh(true)
        },
        onNeedRefresh() {
          setAvailable(true)
          previousFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null
          onUpdateDownloadChange?.(false)
          // Small UX delay so the prompt doesn't pop instantly on page load.
          if (needRefreshDelayRef.current) return

          needRefreshDelayRef.current = window.setTimeout(() => {
            setNeedRefresh(true)
            needRefreshDelayRef.current = null
          }, 1000)
        },
        onRegisterError(err) {
          onUpdateDownloadChange?.(false)
          console.warn('[pwa] SW registration error:', err)
        },
      })
    } catch (err) {
      onUpdateDownloadChange?.(false)
      console.warn('[pwa] SW registration skipped:', err)
    }

    return () => {
      stopAnswering()
      setApplying(false)
      if (needRefreshDelayRef.current) {
        window.clearTimeout(needRefreshDelayRef.current)
        needRefreshDelayRef.current = null
      }

      if (stopWatchingUpdate) {
        stopWatchingUpdate()
        stopWatchingUpdate = null
      }

      onUpdateDownloadChange?.(false)
    }
  }, [onUpdateDownloadChange, setAvailable, setApplying])

  const show = needRefresh && isUpdateSafePath(pathname)

  useEffect(() => {
    if (show) {
      setPresent(true)
      setClosing(false)
      return
    }

    if (!present) return

    setClosing(true)
    const t = window.setTimeout(() => {
      setPresent(false)
      setClosing(false)
    }, ANIM_MS)

    return () => window.clearTimeout(t)
  }, [show, present])

  if (!present) return null

  return createPortal(
    <div
      ref={panelRef}
      inert={closing || undefined}
      aria-hidden={closing || undefined}
      aria-busy={applying}
      className={`${styles.pwaUpdate}${closing ? ` ${styles.closing}` : ''}`}
      data-testid="pwa-update-panel"
      role="status"
      aria-live="polite"
    >
      <ModalContent className={styles.panel}>
        <ModalTitle>{t('runtime.pwaUpdate.title')}</ModalTitle>

        <div style={{ marginTop: 6 }}>{t('runtime.pwaUpdate.body')}</div>

        {reason && <p role="alert">{t('runtime.pwaUpdate.' + reason)}</p>}
        <FormLine style={{ justifyContent: 'flex-end', marginTop: 12 }}>
          <Button
            type="button"
            variant="ghost"
            disabled={applying}
            onClick={() => {
              setNeedRefresh(false)
              setReason(null)
              const target = previousFocus.current
              const visible =
                target &&
                target !== document.body &&
                target.isConnected &&
                target.getClientRects().length > 0
              ;(visible ? target : document.getElementById('play-content'))?.focus({
                preventScroll: true,
              })
            }}
          >
            {t('runtime.pwaUpdate.later')}
          </Button>

          <Button
            type="button"
            disabled={applying}
            data-testid="pwa-update-apply"
            onClick={() => {
              if (!canReloadForUpdate()) {
                setReason('busy')
                return
              }
              if (activatedRef.current) {
                reloadForUpdate()
                return
              }
              const worker = registrationRef.current?.waiting
              if (!worker) {
                setReason('unavailable')
                return
              }
              setApplying(true)
              acceptedRef.current = true
              void requestWaitingUpdate(worker).then(result => {
                if (!result.ok) {
                  acceptedRef.current = false
                  setApplying(false)
                  setReason(result.reason || 'unavailable')
                  window.requestAnimationFrame(() =>
                    panelRef.current
                      ?.querySelector<HTMLButtonElement>('[data-testid="pwa-update-apply"]')
                      ?.focus({ preventScroll: true }),
                  )
                }
              })
            }}
            style={{ marginLeft: 8 }}
          >
            {t('runtime.pwaUpdate.action')}
          </Button>
        </FormLine>
      </ModalContent>
    </div>,
    document.body,
  )
}
